//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useEffect, useLayoutEffect, useState, useRef, useContext, useCallback } from 'react';
import { withStyles } from '@material-ui/core/styles';
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown';
import SimpleBar from 'simplebar-react';
import 'simplebar/dist/simplebar.min.css';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { AppContext, LayoutContext } from '../../common/contexts';
import { unsubscribeRealtimeUpdates } from '../../system/firebase';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { isAtBottomByContentDimensions } from '../../common/scrollHelper';
import { USER_TYPE_EMP, USER_TYPE_JS } from '../../common/constants';
import { getUniqueKey } from '../../common/arrayHelper';
import { loadThreadDetail, loadThreadMessages } from '../../common/services/myMessagesServices';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { addThreadMessage } from '../../common/services/empMyMessagesServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdFab from '../../common/elemental/AfitdFab';
import AfitdBadgeCount from '../../common/elemental/AfitdBadgeCount';
import ThreadMenuButtons from '../../dashboardMyMessages/subComponents/ThreadMenuButtons';
import MessageReply from '../../dashboardMyMessages/subComponents/MessageReply';
import ThreadDetailEmpty from '../../dashboardMyMessages/subComponents/ThreadDetailEmpty';
import MessagesDate from '../../dashboardMyMessages/subComponents/MessagesDate';
import ThreadDetailNoThreads from '../../dashboardMyMessages/subComponents/ThreadDetailNoThreads';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import NewMessageSign from './NewMessageSign';
import MessageBlock from './MessageBlock';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import cx from 'clsx';
import _get from 'lodash/get';
import _set from 'lodash/set';
import _map from 'lodash/map';
import _reduce from 'lodash/reduce';
import _throttle from 'lodash/throttle';
import _trim from 'lodash/trim';
import _keys from 'lodash/keys';
import _includes from 'lodash/includes';
import { startOfDay, isEqual, /*parse as parseDate,*/ isAfter } from 'date-fns';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const styles = theme => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
    },
    threadDetailContainer: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
    },
    chatRightBlock1: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '24px',
        borderBottom: '1px solid #f1f2f6',
        [theme.breakpoints.down('sm')]: { paddingLeft: 10 },
    },
    threadPaneHeader: {
        [theme.breakpoints.down('sm')]: {
            display: 'flex',
            alignItems: 'center',
        },
    },
    subjectText: {
        fontSize: '18px',
        fontWeight: 600,
        lineHeight: '22px',
    },
    chatRightBlock2: {
        position: 'relative',
        flexGrow: 1,
        padding: '21px 23px 0 23px',
        overflowY: 'hidden',
    },
    chatRightBlock3: {
        padding: '48px 0 21px 0',
        [theme.breakpoints.down('sm')]: { padding: '10px 0 10px 0' },
    },
    hidden: {
        display: 'none',
    },

    inline: { display: 'flex', alignItems: 'center' },
    nameText: { marginRight: 10 },
    verifyIcon: { color: '#1D8A8B', margin: '0 5px' },
    navigationIconContainer: {
        width: '32px',
        height: '32px',
        background: '#F2F2F4',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        '&:hover': { cursor: 'pointer' },
        marginRight: 16,
    },
    navIconLeft: { marginLeft: 11, width: 18 },
});

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        props,
        userId,
        // employerId,
        companyProfilePicUrl,
        threadData,
        threadMessages,
        setThreadMessages,
        setSnackbarMessage,
        setSnackbarErrorMessage,
        lastReadDtRef,
        messageListEl,
        reportedDt,
        markedForFollowUpDt,
        blockedDt,
        jsAccountDeleteDt,
        members,
        setIsRequestScrollToBottomRef,
        setNewMessageSignRef,
        isAtThreadBottom,
        messagesCountWhenScrolledUp,
    } = params;
    const { classes, threadId } = props;
    const { threadDetailContainer: threadDetailContainerCls, hidden: hiddenCls } = classes;

    const lastReadDt = _get(lastReadDtRef, 'current');

    const jobSeekerId = _get(threadData, 'jobSeekerId');
    const jobSeekerName = _get(threadData, 'jobSeekerName');
    const jobSeekerProfilePicUrl = _get(threadData, 'jobSeekerProfilePicUrl');
    const subject = _get(threadData, 'jobTitle');

    const contentWrapperEl = _get(messageListEl, 'current.contentWrapperEl');
    const contentEl = _get(messageListEl, 'current.contentEl');
    const bottomNewMessagesCount = threadMessages.length - _get(messagesCountWhenScrolledUp, 'current', threadMessages.length);

    const messageReplyDisabled = !!blockedDt || !!jsAccountDeleteDt;

    const defns = {
        threadDetailContainer: {
            className: cx(threadDetailContainerCls, { [hiddenCls]: !threadId }),
        },
        nameText: {
            variant: 'body2',
            clsMidGrayColor: true,
            value: jobSeekerName,
        },
        threadMenuButtons: {
            // threadMessages,
            threadId,
            reportedDt,
            markedForFollowUpDt,
            blockedDt,
        },
        subjectText: {
            value: subject,
        },
        messageList: { companyProfilePicUrl, jobSeekerProfilePicUrl, members, threadMessages, lastReadDt, newMessageSignRef: setNewMessageSignRef },
        messageReply: {
            disabled: messageReplyDisabled,
            onMessageSend: handleSendMessage.bind(
                null,
                userId,
                threadId,
                jobSeekerId,
                jobSeekerName,
                threadMessages,
                setThreadMessages,
                lastReadDtRef,
                setSnackbarMessage,
                setSnackbarErrorMessage,
            ),
            onFileUploadComplete: handleCompleteFileUpload.bind(
                null,
                userId,
                threadId,
                jobSeekerId,
                jobSeekerName,
                threadMessages,
                setThreadMessages,
                lastReadDtRef,
                setSnackbarMessage,
                setSnackbarErrorMessage,
            ),
            setIsRequestScrollToBottomRef,
        },
        goToBottomButton: {
            isHidden: isAtThreadBottom || threadMessages.length === 0,
            size: 'small',
            style: {
                position: 'absolute',
                right: 30,
                bottom: 10,
            },
            onClick: handleClickGoToBottomButton.bind(null, contentWrapperEl, contentEl, messagesCountWhenScrolledUp),
        },
        bottomNewMessagesCount: {
            className: cx({ [hiddenCls]: isAtThreadBottom || bottomNewMessagesCount <= 0 }),
            style: {
                position: 'absolute',
                right: 55,
                bottom: 35,
                zIndex: 1,
            },
        },
        bottomNewMessagesCountText: {
            count: bottomNewMessagesCount,
        },
    };

    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const handleClickGoToBottomButton = (contentWrapperEl, contentEl, messagesCountWhenScrolledUp) => {
    contentWrapperEl.scrollTop = contentEl.offsetHeight; // scroll to bottom
    messagesCountWhenScrolledUp.current = undefined;
};

const MessageList = props => {
    const { companyProfilePicUrl, jobSeekerProfilePicUrl, threadMessages = [], members, lastReadDt, newMessageSignRef } = props;
    let messageDate = new Date();
    let isNewMessageSignAlreadyDisplayed = false;
    let unreadMessagesCount = 0;

    unreadMessagesCount = _reduce(
        threadMessages,
        (count, message) => {
            const { senderId, createDt } = message;
            const isSenderEmployer = _includes(_keys(members), senderId);
            if (isAfter(createDt, lastReadDt) && !isSenderEmployer) {
                return count + 1;
            } else {
                return count;
            }
        },
        0,
    );

    const getMessageList = () =>
        _map(threadMessages, (message, idx) => {
            const { content, createDt, attachment, senderId, jobSeekerId } = message;

            const isEmployer = !!jobSeekerId; // if jobSeekerId exists in message, this msg was sent by employer

            let isDisplayDate = false;
            let isShowNewMessageSign = false;

            const messageDefn = {
                content,
                createDt,
                senderProfileImageUrl: isEmployer ? _get(members, `${senderId}.profilePicUrl`) : jobSeekerProfilePicUrl,
                companyImage: companyProfilePicUrl,
                type: isEmployer ? USER_TYPE_EMP : USER_TYPE_JS,
                attachment,
                key: `message-${idx}`,
            };

            if (createDt) {
                const createDate = startOfDay(createDt);
                if (!isEqual(createDate, messageDate)) {
                    isDisplayDate = true;
                }
                messageDate = createDate;

                if (unreadMessagesCount > 0 && !jobSeekerId && isAfter(createDt, lastReadDt) && !isNewMessageSignAlreadyDisplayed) {
                    isShowNewMessageSign = true;
                    isNewMessageSignAlreadyDisplayed = true;
                }
            }

            const messageComponents = [];
            if (isDisplayDate) {
                const dateDefn = { messageDate, key: `message-date-${idx}` };
                messageComponents.push(<MessagesDate {...dateDefn} />);
            }

            if (isShowNewMessageSign) {
                messageComponents.push(
                    <NewMessageSign unreadMessagesCount={unreadMessagesCount} newMessageSignRef={newMessageSignRef} key={`new-message-sign-${getUniqueKey()}`} />,
                );
            }
            messageComponents.push(<MessageBlock {...messageDefn} />);

            return messageComponents;
        });

    return <div>{getMessageList()}</div>;
};

const setLoadThreadDetailUnsubscribeRef = (ref, unsubscribe) => {
    ref.current = unsubscribe;
};
const detachThreadDetailListener = ref => {
    if (ref.current) {
        // call unsubscribe
        ref.current();
    }
};

const setLoadThreadMessagesUnsubscribeRef = (ref, unsubscribe) => {
    ref.current = unsubscribe;
};
const detachThreadMessagesListener = ref => {
    if (ref.current) {
        // call unsubscribe
        ref.current();
    }
};

const handleSendMessage = (
    userId,
    threadId,
    jobSeekerId,
    jobSeekerName,
    threadMessages,
    setThreadMessages,
    lastReadDtRef,
    setSnackbarMessage,
    setSnackbarErrorMessage,
    message,
    evt,
) => {
    const trimmedMessage = _trim(message);
    let messageDto = {
        createDt: new Date(),
        senderId: userId,
        content: trimmedMessage,
        jobSeekerId,
        threadId,
    };
    setThreadMessages([...threadMessages, messageDto]);
    addThreadMessage({ messageDto, setSnackbarMessage, setSnackbarErrorMessage, recipientName: jobSeekerName, lastReadDtRef });
};

const handleCompleteFileUpload = (
    userId,
    threadId,
    jobSeekerId,
    jobSeekerName,
    threadMessages,
    setThreadMessages,
    lastReadDtRef,
    setSnackbarMessage,
    setSnackbarErrorMessage,
    message,
    filename,
    fileType,
    fileSize,
    fileUrl,
    evt,
) => {
    let messageDto = {
        createDt: new Date(),
        senderId: userId,
        jobSeekerId,
        threadId,
        attachment: {
            title: filename,
            size: fileSize,
            type: fileType,
            url: fileUrl,
        },
    };

    if (message) {
        _set(messageDto, 'content', message);
    }
    setThreadMessages([...threadMessages, messageDto]);
    addThreadMessage({ messageDto, setSnackbarMessage, setSnackbarErrorMessage, recipientName: jobSeekerName, lastReadDtRef });
};

const handleScrollMessageListEl = (lastContentDimentionsRef, setIsAtThreadBottom, evt) => {
    const contentWrapperEl = evt.target;
    const lastContentDimentions = _get(lastContentDimentionsRef, 'current');
    if (contentWrapperEl) {
        const isAtBottom = isAtBottomByContentDimensions({ containerEl: contentWrapperEl, contentDimensions: lastContentDimentions });
        setIsAtThreadBottom(isAtBottom);
    }
};

const getHandleScrollMessageListEl = _throttle(handleScrollMessageListEl, 500, { leading: true });

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const ThreadDetail = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const { userId, setCurrentThreadId, setSnackbarMessage, setSnackbarErrorMessage } = useContext(LayoutContext);
    const { employerId, currentEmployer } = useContext(AppContext);
    const companyProfilePicUrl = _get(currentEmployer, 'profilePicUrl');

    const threadIdRef = useRef();
    const lastReadDtRef = useRef();
    const loadThreadDetailUnsubscribeRef = useRef();
    const loadThreadMessagesUnsubscribeRef = useRef();
    const messageListEl = useRef();
    const lastContentDimentionsRef = useRef();
    const isRequestInitialScrollRef = useRef(false);
    const isRequestScrollToBottomRef = useRef(false);
    const newMessageSignRef = useRef();
    const messagesCountWhenScrolledUp = useRef();
    const theme = useTheme();
    const smMatches = useMediaQuery(theme.breakpoints.down('sm'));

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [threadData, setThreadData] = useState();
    const [threadMessages, setThreadMessages] = useState([]);
    const [isAtThreadBottom, setIsAtThreadBottom] = useState();

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { myMessagesId, threadId, lastReadDt, deleteDt, setShowMessageDetails, threadCount } = props;

    //---------------------------------------------------------------------
    // useCallback hook
    //---------------------------------------------------------------------
    const setIsRequestScrollToBottomRef = useCallback(
        value => {
            isRequestScrollToBottomRef.current = value;
        },
        [isRequestScrollToBottomRef],
    );
    const setNewMessageSignRef = useCallback(
        ref => {
            newMessageSignRef.current = ref;
        },
        [newMessageSignRef],
    );
    // console.log('threadData', threadData);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const reportedDt = _get(threadData, 'reportedDt');
    const markedForFollowUpDt = _get(threadData, 'followUpDt');
    const blockedDt = _get(threadData, 'blockedDt');
    const jsAccountDeleteDt = _get(threadData, 'jsAccountDeleteDt');
    const members = _get(threadData, 'members');

    const contentWrapperEl = _get(messageListEl, 'current.contentWrapperEl');
    const contentEl = _get(messageListEl, 'current.contentEl');

    //---------------------------------------------------------------------
    // useCallback
    //---------------------------------------------------------------------
    const clearThreadDetail = useCallback(() => {
        setThreadData(null);
        setThreadMessages([]);
        threadIdRef.current = undefined;
        lastReadDtRef.current = undefined;
        messagesCountWhenScrolledUp.current = undefined;
    }, []);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        // only call db only if it is a different thread Id
        if (threadId && threadId !== threadIdRef.current) {
            // clear some states and ref first
            messagesCountWhenScrolledUp.current = undefined;

            // load the thread document
            detachThreadDetailListener(loadThreadDetailUnsubscribeRef);
            const unsubscribeLoadThreadDetailOnSnapshot = loadThreadDetail({
                viewerId: employerId,
                myMessagesId,
                threadId,
                setThreadData,
                setSnackbarErrorMessage,
            });
            setLoadThreadDetailUnsubscribeRef(loadThreadDetailUnsubscribeRef, unsubscribeLoadThreadDetailOnSnapshot);

            detachThreadMessagesListener(loadThreadMessagesUnsubscribeRef);
            const unsubscribeLoadThreadMessagesOnSnapshot = loadThreadMessages({
                viewerId: employerId,
                myMessagesId,
                threadId,
                deleteDt,
                setThreadMessages,
                setSnackbarErrorMessage,
            });
            setLoadThreadMessagesUnsubscribeRef(loadThreadMessagesUnsubscribeRef, unsubscribeLoadThreadMessagesOnSnapshot);

            // remember about the loaded thread so we can check and prevent calling db or changing lastReadDt on every prop change
            threadIdRef.current = threadId;
            lastReadDtRef.current = lastReadDt;

            // set the current thread Id
            setCurrentThreadId(threadId);
            isRequestInitialScrollRef.current = true;

            // unsubscribe realtime update on unmount
            return () => {
                unsubscribeRealtimeUpdates([unsubscribeLoadThreadDetailOnSnapshot, unsubscribeLoadThreadMessagesOnSnapshot]);
            };
        } else if (!threadId) {
            // clear thread detail
            clearThreadDetail();
        }
    }, [userId, employerId, myMessagesId, threadId, lastReadDt, deleteDt, setCurrentThreadId, messageListEl, clearThreadDetail, setSnackbarErrorMessage]);

    useLayoutEffect(() => {
        // once messageListEl is available, set up scroll event listener
        if (contentWrapperEl) {
            contentWrapperEl.addEventListener('scroll', getHandleScrollMessageListEl.bind(null, lastContentDimentionsRef, setIsAtThreadBottom));
        }

        // remove scroll event listener when unmount
        return () => {
            if (contentWrapperEl) {
                contentWrapperEl.removeEventListener('scroll', getHandleScrollMessageListEl.bind(null, lastContentDimentionsRef, setIsAtThreadBottom));
            }
        };
    }, [contentWrapperEl, lastContentDimentionsRef]);

    useEffect(() => {
        // if scrolling up from the bottom and messagesCountWhenScrolledUp has not yet been set
        if (isAtThreadBottom === false && _get(messagesCountWhenScrolledUp, 'current') === undefined) {
            // remember the messages count at that time
            messagesCountWhenScrolledUp.current = threadMessages.length;
        } else if (isAtThreadBottom === true) {
            messagesCountWhenScrolledUp.current = undefined;
        }
    }, [isAtThreadBottom, threadMessages]);

    useEffect(() => {
        const isRequestInitialScroll = _get(isRequestInitialScrollRef, 'current');
        const isRequestScrollToBottom = _get(isRequestScrollToBottomRef, 'current');
        const lastContentDimentions = _get(lastContentDimentionsRef, 'current');

        // Scroll to message
        if (contentWrapperEl && contentEl) {
            if (isRequestInitialScroll) {
                // For initial scroll, scroll to first new message or if no new message, scroll to bottom
                const newMessageSignEl = _get(newMessageSignRef, 'current');
                if (newMessageSignEl) {
                    contentWrapperEl.scrollTop = newMessageSignEl.offsetTop; // scroll to first new message
                } else {
                    contentWrapperEl.scrollTop = contentEl.offsetHeight; // scroll to bottom
                    messagesCountWhenScrolledUp.current = undefined;
                }
                isRequestInitialScrollRef.current = false; // reset flag
            } else if (isRequestScrollToBottom || isAtBottomByContentDimensions({ containerEl: contentWrapperEl, contentDimensions: lastContentDimentions })) {
                // If requested by replying messages OR if it was already at the bottom of the thread before new messages arrived, scroll to the bottom now
                contentWrapperEl.scrollTop = contentEl.offsetHeight; // scroll to bottom
                isRequestScrollToBottomRef.current = false; // reset flag
                messagesCountWhenScrolledUp.current = undefined;
            }
            lastContentDimentionsRef.current = contentEl.getBoundingClientRect(); // update content dimension ref
        }
    }, [threadMessages, contentWrapperEl, contentEl]);

    //---------------------------------------------------------------------
    // Defns
    //---------------------------------------------------------------------
    const defns = getDefns({
        props,
        userId,
        employerId,
        companyProfilePicUrl,
        threadData,
        threadMessages,
        setThreadMessages,
        setSnackbarMessage,
        setSnackbarErrorMessage,
        lastReadDtRef,
        messageListEl,
        reportedDt,
        markedForFollowUpDt,
        blockedDt,
        jsAccountDeleteDt,
        members,
        setIsRequestScrollToBottomRef,
        setNewMessageSignRef,
        isAtThreadBottom,
        // currentUser,
        messagesCountWhenScrolledUp,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div {...defns.root}>
            <div {...defns.threadDetailContainer}>
                <div {...defns.chatRightBlock1}>
                    <div {...defns.threadPaneHeader}>
                        {smMatches ? (
                            <div {...defns.navigationIconContainer} onClick={() => setShowMessageDetails(false)}>
                                <ArrowBackIosIcon {...defns.navIconLeft} />
                            </div>
                        ) : null}
                        <div>
                            <div {...defns.inline}>
                                <AfitdTypography {...defns.nameText} />
                            </div>
                            <AfitdTypography {...defns.subjectText} />
                        </div>
                    </div>
                    <div>
                        <ThreadMenuButtons {...defns.threadMenuButtons} />
                    </div>
                </div>
                <div {...defns.chatRightBlock2}>
                    <SimpleBar style={{ height: '100%' }} ref={messageListEl}>
                        <MessageList {...defns.messageList} />
                    </SimpleBar>
                    <div {...defns.bottomNewMessagesCount}>
                        <AfitdBadgeCount {...defns.bottomNewMessagesCountText} />
                    </div>
                    <AfitdFab aria-label="Go to Bottom" {...defns.goToBottomButton}>
                        <KeyboardArrowDown />
                    </AfitdFab>
                </div>
                <div {...defns.chatRightBlock3}>
                    <MessageReply {...defns.messageReply} />
                </div>
            </div>
            {threadCount === 0 && <ThreadDetailNoThreads />}
            {threadCount > 0 && !threadId && <ThreadDetailEmpty />}
        </div>
    );
};

export default withStyles(styles)(ThreadDetail);
